import SystemHelper from 'features/System/systemHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import DialogAddPermissionGroupView from '../DialogAddPermissionGroupView';
import KTPagination, { KTPaginationColor } from 'general/components/OtherKeenComponents/KTPagination';
import AppConfigs from 'general/constants/AppConfigs';
import { ChargeConstants } from 'features/Charge/chargeHelper';
import AppConstants from 'general/constants/AppConstants';

PermissionGroupListView.propTypes = {
  showFullUI: PropTypes.bool,
  prefKey: PropTypes.string,
  role: PropTypes.string,
  subRole: PropTypes.string,
};

const defaultColumnIds = ['CODE', 'NAME', 'DESCRIPTION', 'PERMISSION_COUNT'];

function PermissionGroupListView({ showFullUI, prefKey, role, subRole }) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { user } = useSelector((state) => state.auth);
  const adminRole =
    user?.role === AppConstants.userRole.admin || user?.role === AppConstants.userRole.operator;
  const [filter, setFilter] = useState();
  useEffect(() => {
    setFilter({
      role: role ? role : adminRole ? 'ADMIN' : 'AGENCY',
      subRole: subRole ? subRole : !adminRole ? user?.agency?.primaryRole : '',
      page: 0,
      limit: AppConfigs.defaultPageLimit,
    });
  }, [role, subRole, adminRole, user]);
  const [permissionGroupList, setPermissionGroupList] = useState([]);
  const [permissionList, setPermissionList] = useState([]);
  useEffect(() => {
    if (Utils.isValid(filter)) {
      let params = _.pickBy(filter, (value) => {
        return value !== undefined && value !== null && value !== '';
      });
      SystemHelper.API.Permission.requestGetPermissionGroupList(params).then((response) => {
        const sorted = _.orderBy(response.permissionGroups, [(g) => g.createdAt ?? ''], ['asc']);
        setPermissionGroupList(sorted);
        setPermissionList(response.permissions);
      });
    }
  }, [filter]);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'CODE',
        name: UIHelper.buildTableColumn('Mã nhóm quyền'),
        selector: (row) => (
          <span className="d-flex align-items-center gap-2">
            {row?.permissionGroupCode ?? `RG${row?.permissionGroupId?.toString().padStart(8, '0')}`}
          </span>
        ),
        width: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'NAME',
        name: UIHelper.buildTableColumn('Tên nhóm quyền'),
        selector: (row) => <span className="d-flex align-items-center gap-2">{row?.name}</span>,
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'DESCRIPTION',
        name: UIHelper.buildTableColumn('Mô tả'),
        selector: (row) => (
          <span className="d-flex align-items-center gap-2">{row?.description}</span>
        ),
        minWidth: '240px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'PERMISSION_COUNT',
        name: UIHelper.buildTableColumn('Số lượng quyền'),
        selector: (row) => (
          <span className="d-flex align-items-center gap-2">
            {row?.permissions?.length > 0 ? Utils.formatNumber(row?.permissions?.length) : 0}
          </span>
        ),
        width: '130px',
        right: true,
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        maxWidth: '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        cell: (row) => {
          return UIHelper.buildMoreActionsButton(buildMoreActions(row), true);
        },
      },
    ];
  }, [user, permissionList]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);

  // MARK: ---- METHODS ----
  const buildMoreActions = (row) => {
    const actions = [];
    // xem chi tiết
    actions.push({
      text: 'Xem chi tiết',
      iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
      click: () => {
        handleAddNew(row);
      },
    });
    // chinh sua
    if (adminRole) {
      actions.push({
        text: 'Chỉnh sửa',
        iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          handleAddNew(row);
        },
      });
    }
    // xoa
    if (adminRole) {
      actions.push({
        text: 'Xoá',
        iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          SystemHelper.UI.Permission.showDialogConfirmDeletePermissionGroup(row, () => {
            handleRefresh({ ...filter });
          });
        },
      });
    }
    return actions;
  };
  const handleRefresh = (newFilter) => {
    setFilter(newFilter);
  };
  const handleAddNew = (row) => {
    AppCustomDialogHelper.show(
      `${!!row
        ? adminRole
          ? 'Cập nhật'
          : 'Chi tiết'
        : 'Thêm'
      } nhóm quyền ${!adminRole
        ? 'nhân viên'
        : filter.role === 'ADMIN'
          ? 'nhân viên'
          : filter.subRole === 'SALE'
            ? 'đại lý'
            : 'đối tác'
      }`,
      < DialogAddPermissionGroupView
        role={filter.role}
        subRole={filter.subRole}
        item={row}
        permissionList={permissionList}
        onCancel={() => AppCustomDialogHelper.hide()
        }
        onDone={() => {
          AppCustomDialogHelper.hide();
          handleRefresh({ ...filter });
        }}
        removeFooter={!adminRole}
      />,
      'lg',
      false,
      true
    );
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div>
          <div className="pb-4">
            {/* header */}
            <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
              {/* title */}
              <div className="d-flex flex-column">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Quản lý nhóm quyền {adminRole && subRole ? (subRole === 'SALE' ? 'đại lý' : 'đối tác') : 'nhân viên'}
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng:{' '}
                  <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                    {Utils.formatNumber(permissionGroupList?.length)} nhóm quyền
                  </span>
                </span>
              </div>

              {/* actions */}
              {adminRole && UIHelper.buildGreenButton(
                'Thêm nhóm quyền',
                handleAddNew,
                <i className="fa-regular fa-circle-plus" />
              )}
            </div>

            {/* table */}
            <div>
              <div className="px-4">
                <KTTable
                  columns={columns}
                  items={permissionGroupList}
                  selectableRows={false}
                  striped={false}
                  compactGrid={false}
                  onClickedRow={(row) => { }}
                  onSelectedRowsChanged={(rows) => { }}
                  onDoubleClickedRow={(row) => { }}
                  noDataComponent={UIHelper.buildEmptyNoData(
                    'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                  )}
                />
              </div>
            </div>

            {/* pagination */}
            <div className="px-4 mt-2">
              <KTPagination
                currentPage={filter?.page + 1}
                rowCount={permissionGroupList?.length}
                rowsPerPage={filter?.limit}
                currentItemInPageCount={permissionGroupList?.length}
                onChangePage={(newPage) => {
                  const iNewPage = parseInt(newPage);
                  setFilter({
                    ...filter,
                    page: iNewPage - 1,
                  })
                  handleRefresh();
                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                  const iNewRowsPerPage = parseInt(newRowsPerPage);
                  setFilter({
                    ...filter,
                    limit: iNewRowsPerPage,
                    page: 0,
                  })
                  handleRefresh();
                }}
                loading={false}
                color={KTPaginationColor.success}
                appendText="nhóm quyền"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PermissionGroupListView;
